import React, { useState, useEffect, useRef } from 'react';
import { Input } from './ui/input';
import { MapPin, Loader2, X } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AddressAutocomplete = ({ 
  value, 
  onChange, 
  onSelect,
  placeholder = "Start typing your address...",
  className = ""
}) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [sessionToken] = useState(() => crypto.randomUUID());
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update input value when external value changes
  useEffect(() => {
    if (value !== inputValue) {
      setInputValue(value || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const fetchSuggestions = async (input) => {
    if (!input || input.length < 3) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/places/autocomplete`, {
        params: { 
          input,
          session_token: sessionToken
        }
      });
      setSuggestions(response.data.predictions || []);
      setShowSuggestions(true);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange && onChange(newValue);

    // Debounce API calls
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      fetchSuggestions(newValue);
    }, 300);
  };

  const handleSelectSuggestion = async (suggestion) => {
    setIsLoading(true);
    setShowSuggestions(false);
    
    try {
      // Get place details to extract postcode
      const response = await axios.get(`${BACKEND_URL}/api/places/details`, {
        params: {
          place_id: suggestion.place_id,
          session_token: sessionToken
        }
      });
      
      const { formatted_address, postcode } = response.data;
      
      setInputValue(formatted_address);
      onChange && onChange(formatted_address);
      onSelect && onSelect({
        address: formatted_address,
        postcode: postcode || '',
        place_id: suggestion.place_id
      });
    } catch (error) {
      console.error('Error fetching place details:', error);
      // Fallback - use the description as address
      setInputValue(suggestion.description);
      onChange && onChange(suggestion.description);
      onSelect && onSelect({
        address: suggestion.description,
        postcode: '',
        place_id: suggestion.place_id
      });
    } finally {
      setIsLoading(false);
    }
  };

  const clearInput = () => {
    setInputValue('');
    setSuggestions([]);
    onChange && onChange('');
    onSelect && onSelect({ address: '', postcode: '', place_id: '' });
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          type="text"
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          className="pl-10 pr-10 h-12 text-base font-medium border-gray-200 focus:border-cyo-orange focus:ring-cyo-orange rounded-lg bg-white"
          data-testid="address-autocomplete-input"
        />
        {isLoading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
        )}
        {!isLoading && inputValue && (
          <button
            type="button"
            onClick={clearInput}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {suggestions.map((suggestion, index) => (
            <button
              key={suggestion.place_id || index}
              type="button"
              onClick={() => handleSelectSuggestion(suggestion)}
              className="w-full px-4 py-3 text-left hover:bg-orange-50 transition-colors flex items-start gap-3 border-b border-gray-100 last:border-b-0"
              data-testid={`suggestion-${index}`}
            >
              <MapPin className="w-4 h-4 text-cyo-orange mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {suggestion.structured_formatting?.main_text || suggestion.description.split(',')[0]}
                </p>
                <p className="text-xs text-gray-500">
                  {suggestion.structured_formatting?.secondary_text || suggestion.description.split(',').slice(1).join(',')}
                </p>
              </div>
            </button>
          ))}
          
          {/* Google attribution - inside dropdown */}
          <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 text-right">
            <img 
              src="https://developers.google.com/maps/documentation/images/powered_by_google_on_white.png" 
              alt="Powered by Google" 
              className="h-3 inline-block"
            />
          </div>
        </div>
      )}
    </div>
  );
};
